import React from "react";
import { LuArrowRight, LuTarget } from "react-icons/lu";
import { FIELD_HINT, PrimaryButton, SelectField } from "../ui";
import NumberField from "./NumberField";
import ChoiceField from "./ChoiceField";
import StepIntro from "./StepIntro";
import {
  ChoiceFieldName,
  Evidence,
  NumberFieldName,
  OBJECTIVES,
  Objective,
  Programme,
  isChoiceField,
} from "./calculator";

type Props = {
  programme: Programme;
  evidence: Evidence;
  onChange: (name: NumberFieldName, value: number) => void;
  onChoiceChange: (name: ChoiceFieldName, value: number) => void;
  onObjectiveChange: (objective: Objective) => void;
};

export default function StepProgramme({
  programme,
  evidence,
  onChange,
  onChoiceChange,
  onObjectiveChange,
}: Props) {
  const objective = OBJECTIVES[programme.objective];
  const objectiveEvidence = evidence[programme.objective];

  return (
    <>
      <StepIntro
        eyebrow="1. Your programme"
        title="What would you like mentoring to change?"
        copy="Start with one objective. We'll use your assumptions to build a focused business case."
      />

      <div className="tw-mt-7">
        <SelectField
          id="roi-objective"
          name="objective"
          label="Primary objective for this programme"
          value={programme.objective}
          options={(Object.keys(OBJECTIVES) as Objective[]).map((key) => ({
            value: key,
            label: OBJECTIVES[key].label,
          }))}
          onChange={(value) => onObjectiveChange(value as Objective)}
        />
      </div>

      <div className="tw-mt-5 tw-grid tw-grid-cols-2 tw-gap-5 xs:tw-grid-cols-1">
        <NumberField name="participants" programme={programme} onChange={onChange} />
        <NumberField name="salary" programme={programme} onChange={onChange} />
      </div>

      <div className="tw-mt-6 tw-rounded-2xl tw-border tw-border-[#c9deed] tw-bg-[#f4f9fd] tw-p-5">
        <div className="tw-flex tw-items-center tw-gap-2">
          <LuTarget aria-hidden className="tw-h-4 tw-w-4 tw-text-[#087bd3]" />
          <h4 className="tw-m-0 tw-font-black tw-text-[#173754]">{objective.label}</h4>
        </div>
        <p className={FIELD_HINT}>
          Enter what you know about your organisation. Outcome assumptions are limited to conservative
          research-informed scenarios; open &ldquo;Why this range?&rdquo; to see the evidence and caveats.
        </p>

        <div className="tw-mt-5 tw-grid tw-gap-5">
          {objective.fields.map((name) =>
            isChoiceField(name) ? (
              <ChoiceField
                key={name}
                name={name}
                value={programme[name]}
                evidence={objectiveEvidence}
                onChange={onChoiceChange}
              />
            ) : (
              <NumberField key={name} name={name} programme={programme} onChange={onChange} />
            )
          )}
        </div>
      </div>

      <div className="tw-mt-6">
        <NumberField name="hourlyRate" programme={programme} onChange={onChange} />
      </div>

      <p className="tw-mt-5 tw-mb-0 tw-text-xs tw-leading-5 tw-text-[#5b7188]">
        Your salary, headcount and hourly rate also start with example values. Replace them with your
        own figures.
      </p>

      <div className="tw-mt-7 tw-flex tw-flex-row tw-items-center tw-gap-4 sm:tw-flex-col sm:tw-items-stretch">
        <PrimaryButton type="submit">
          See my business case
          <LuArrowRight aria-hidden className="tw-h-4 tw-w-4" />
        </PrimaryButton>
        <p className="tw-m-0 tw-text-xs tw-text-[#5b7188]">No payment required.</p>
      </div>
    </>
  );
}
